import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shuffle, Check, Upload, X, Loader2 } from "lucide-react";
import { isHeicImage, normalizeImageFile } from "@/lib/heic";
import { toast } from "@/hooks/use-toast";
import { track } from "@/lib/posthog";

import styleMinimalist from "@/assets/style-minimalism.jpg";
import styleBohemian from "@/assets/style-boho.jpg";
import styleIndustrial from "@/assets/style-industrial.jpg";
import styleTraditional from "@/assets/style-traditional.jpg";
import styleScandinavian from "@/assets/style-scandinavian.jpg";
import styleContemporary from "@/assets/style-contemporary.jpg";
import styleMidcentury from "@/assets/style-midcentury.jpg";
import styleJapandi from "@/assets/style-japandi.jpg";
import styleFarmhouse from "@/assets/style-farmhouse.jpg";
import styleArtDeco from "@/assets/style-art-deco.jpg";
import styleBauhaus from "@/assets/style-bauhaus.jpg";
import styleVictorian from "@/assets/style-victorian.jpg";
import styleCoastal from "@/assets/style-coastal.jpg";
import styleEclectic from "@/assets/style-eclectic.jpg";
import styleMediterranean from "@/assets/style-mediterranean.jpg";
import styleMemphis from "@/assets/style-memphis.jpg";
import styleRegency from "@/assets/style-regency.jpg";
import styleTransitional from "@/assets/style-transitional.jpg";

export type AreaUnit = "sqft" | "sqm";

export interface RoomSize {
  unsure: boolean;
  /** Internal canonical value in square meters. null when unsure or empty. */
  value: number | null;
  /** The unit the user entered the value in (for display). */
  unit: AreaUnit;
}

export type FurnitureApproach = "fresh" | "keep";

export interface Answers {
  purposes: string[];
  budget: string;
  style: string;
  inspirationImages?: string[];
  roomSize?: RoomSize;
  furnitureApproach?: FurnitureApproach;
}

const SQM_PER_SQFT = 0.092903;
const SQFT_PER_SQM = 10.7639;

const SIZE_CHIPS: Record<AreaUnit, { label: string; hint: string; midpoint: number }[]> = {
  sqft: [
    { label: "< 100 sq ft", hint: "Powder room or walk-in closet", midpoint: 75 },
    { label: "100–200 sq ft", hint: "Small bedroom or home office", midpoint: 150 },
    { label: "200–400 sq ft", hint: "Standard bedroom or small living room", midpoint: 300 },
    { label: "400–600 sq ft", hint: "Large primary bedroom or main living room", midpoint: 500 },
    { label: "600+ sq ft", hint: "Open-plan space or great room", midpoint: 750 },
  ],
  sqm: [
    { label: "< 10 sq m", hint: "Powder room or walk-in closet", midpoint: 7 },
    { label: "10–20 sq m", hint: "Small bedroom or home office", midpoint: 15 },
    { label: "20–40 sq m", hint: "Standard bedroom or small living room", midpoint: 30 },
    { label: "40–60 sq m", hint: "Large primary bedroom or main living room", midpoint: 50 },
    { label: "60+ sq m", hint: "Open-plan space or great room", midpoint: 75 },
  ],
};

interface QuestionnaireProps {
  preview: string;
  onComplete: (answers: Answers) => void | Promise<void>;
  onBack: () => void;
  /** Preselected style, e.g. from "Use this style" in the feed. */
  initialStyle?: string;
}

const purposeOptions = [
  "Work / Focus",
  "Sleep / Rest",
  "Eat / Dine",
  "Relax / Unwind",
  "Socialize / Entertain",
  "Exercise / Move",
];

const stylesPage1 = [
  { name: "Minimalist", image: styleMinimalist },
  { name: "Bohemian", image: styleBohemian },
  { name: "Industrial", image: styleIndustrial },
  { name: "Traditional", image: styleTraditional },
  { name: "Scandinavian", image: styleScandinavian },
  { name: "Contemporary", image: styleContemporary },
  { name: "Mid-Century", image: styleMidcentury },
  { name: "Japandi", image: styleJapandi },
  { name: "Farmhouse", image: styleFarmhouse },
];

const stylesPage2 = [
  { name: "Art Deco", image: styleArtDeco },
  { name: "Bauhaus", image: styleBauhaus },
  { name: "Victorian", image: styleVictorian },
  { name: "Coastal", image: styleCoastal },
  { name: "Eclectic", image: styleEclectic },
  { name: "Mediterranean", image: styleMediterranean },
  { name: "Memphis", image: styleMemphis },
  { name: "Regency", image: styleRegency },
  { name: "Transitional", image: styleTransitional },
];

const allStylePages = [stylesPage1, stylesPage2];

const Questionnaire = ({ preview, onComplete, onBack, initialStyle }: QuestionnaireProps) => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [stylePage, setStylePage] = useState(0);
  const [styleTab, setStyleTab] = useState<"presets" | "inspiration">("presets");
  const areaUnit: AreaUnit = "sqft";
  const [answers, setAnswers] = useState<Answers>({
    purposes: [],
    style: initialStyle ?? "",
    // Kept for backend contract; filtering now happens client-side on results.
    budget: "Show me your best options",
    inspirationImages: [],
    roomSize: { unsure: false, value: null, unit: "sqft" },
  });

  const selectChip = (midpoint: number, label: string) => {
    const sqm = areaUnit === "sqft" ? midpoint * SQM_PER_SQFT : midpoint;
    setAnswers((p) => ({ ...p, roomSize: { unsure: false, value: sqm, unit: areaUnit } }));
    track("questionnaire_option_selected", {
      step_number: 2,
      step_name: "room_size",
      option_value: label,
    });
  };

  const setUnsure = () => {
    setAnswers((p) => ({ ...p, roomSize: { unsure: true, value: null, unit: areaUnit } }));
    track("questionnaire_option_selected", {
      step_number: 2,
      step_name: "room_size",
      option_value: "unsure",
    });
  };



  const togglePurpose = (purpose: string) => {
    setAnswers((p) => {
      const isAdding = !p.purposes.includes(purpose);
      if (isAdding) {
        track("questionnaire_option_selected", {
          step_number: 1,
          step_name: "purposes",
          option_value: purpose,
        });
      }
      return {
        ...p,
        purposes: isAdding
          ? [...p.purposes, purpose]
          : p.purposes.filter((x) => x !== purpose),
      };
    });
  };

  const handleInspirationFiles = useCallback(async (files: FileList | File[]) => {
    const checks = await Promise.all(
      Array.from(files).map(async (file) => ({ file, isHeic: await isHeicImage(file) }))
    );
    const arr = checks
      .filter(({ file, isHeic }) => (file.type.startsWith("image/") || isHeic) && file.size <= 20 * 1024 * 1024)
      .map(({ file }) => file);

    if (arr.length > 0) {
      track("questionnaire_option_selected", {
        step_number: 3,
        step_name: "style",
        option_value: "inspiration_upload",
      });
    }

    arr.forEach(async (rawFile) => {
      let file: File;
      try {
        file = await normalizeImageFile(rawFile);
      } catch (error) {
        toast({
          title: "Couldn't load HEIC image",
          description: error instanceof Error ? error.message : "Please upload the image as JPG or PNG.",
          variant: "destructive",
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setAnswers((p) => {
          const current = p.inspirationImages || [];
          if (current.length >= 3) return p;
          return { ...p, inspirationImages: [...current, dataUrl] };
        });
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const removeInspiration = (idx: number) => {
    setAnswers((p) => ({
      ...p,
      inspirationImages: (p.inspirationImages || []).filter((_, i) => i !== idx),
    }));
  };

  const hasInspiration = (answers.inspirationImages?.length ?? 0) > 0;

  const roomSizeOk =
    !!answers.roomSize &&
    (answers.roomSize.unsure || (answers.roomSize.value != null && answers.roomSize.value > 0));

  const canProceed = Boolean(
    (step === 0 && answers.purposes.length > 0) ||
    (step === 1 && roomSizeOk) ||
    (step === 2 && (answers.style || hasInspiration)) ||
    (step === 3 && answers.furnitureApproach)
  );

  useEffect(() => {
    track("questionnaire_started", {});
  }, []);

  const handleNext = () => {
    if (submitting) return;
    track("questionnaire_step_completed", { step_number: step + 1 });
    if (step < 3) {
      setStep(step + 1);
    } else {
      setSubmitting(true);
      track("questionnaire_completed", { total_steps: 4 });
      // Let the button repaint with its loading state before any heavy work.
      requestAnimationFrame(() => {
        setTimeout(() => {
          void Promise.resolve().then(() => onComplete(answers)).finally(() => setSubmitting(false));
        }, 0);
      });
    }
  };

  const nextLabel =
    step === 3 ? (
      submitting ? (
        <span className="inline-flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Starting your design…
        </span>
      ) : (
        "Generate design"
      )
    ) : (
      "Continue"
    );

  const handlePrev = () => {
    if (step > 0) setStep(step - 1);
    else onBack();
  };

  const displayedStyles = allStylePages[stylePage];

  const stepConfig = [
    {
      label: "Step 1 of 4",
      title: "What will this room be used for?",
      subtitle: "Select all the activities this space needs to support. You can pick more than one — perfect for multi-function rooms.",
    },
    {
      label: "Step 2 of 4",
      title: "Estimate to the best of your abilities — how big is the room?",
      subtitle: "A rough size helps us recommend furniture that actually fits and renders proportions more accurately. Not sure? No problem — just pick \"Unsure\".",
    },
    {
      label: "Step 3 of 4",
      title: "Choose your style.",
      subtitle: "Select a foundational aesthetic. Our AI will use this as the baseline for generation.",
    },
    {
      label: "Step 4 of 4",
      title: "Start fresh, or work with what you have?",
      subtitle: "Tell us how attached you are to your current furniture. You can always fine-tune each piece after the design is ready.",
    },
  ];

  const furnitureApproachOptions: {
    value: FurnitureApproach;
    title: string;
    description: string;
  }[] = [
    {
      value: "fresh",
      title: "Start fresh",
      description: "Clear the room and design it from scratch with all-new pieces.",
    },
    {
      value: "keep",
      title: "Keep what I have",
      description: "Build around your existing furniture where it works.",
    },
  ];

  const selectFurnitureApproach = (value: FurnitureApproach) => {
    setAnswers((p) => ({ ...p, furnitureApproach: value }));
    track("questionnaire_option_selected", {
      step_number: 4,
      step_name: "furniture_approach",
      option_value: value,
    });
  };


  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="w-full max-w-[1200px] mx-auto"
    >
      <div className="grid lg:grid-cols-[1fr_2fr] gap-8 lg:gap-20">
        {/* Left Column */}
        <div className="flex flex-col justify-center">
          {/* Room thumbnail */}
          <div className="mb-6">
            <img
              src={preview}
              alt="Your room"
              className="w-20 h-20 object-cover rounded-lg border border-border"
            />
          </div>

          <p className="eyebrow mb-4">
            {stepConfig[step].label}
          </p>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <h2
                className="font-display text-2xl md:text-4xl font-medium text-foreground leading-[1.1] mb-5"
                style={{ letterSpacing: "-0.88px" }}
              >
                {stepConfig[step].title}
              </h2>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-6 md:mb-8">
                {stepConfig[step].subtitle}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Navigation - desktop only */}
          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={handlePrev}
              className="px-8 py-4 rounded-full border border-foreground text-foreground text-[13px] font-semibold tracking-normal hover:bg-foreground hover:text-background transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={!canProceed || submitting} aria-busy={submitting}
              className="px-8 py-4 rounded-full bg-primary-strong text-primary-strong-foreground text-[13px] font-semibold tracking-normal shadow-elegant hover:opacity-90 transition-opacity disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none disabled:cursor-not-allowed"
            >
              {nextLabel}
            </button>
          </div>

          {/* Step Indicators - desktop */}
          <div className="hidden lg:flex items-center gap-3 mt-8">
            {[0, 1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-px transition-all ${
                  s <= step
                    ? "w-10 bg-foreground"
                    : "w-6 bg-foreground/20"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Right Column - Content */}
        <div>
          <AnimatePresence mode="wait">
            {/* Step 1: Room Purposes (multi-select) */}
            {step === 0 && (
              <motion.div
                key="purposes"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-2 gap-3 md:gap-4"
              >
                {purposeOptions.map((purpose) => {
                  const selected = answers.purposes.includes(purpose);
                  return (
                    <button
                      key={purpose}
                      onClick={() => togglePurpose(purpose)}
                      className={`relative text-left px-5 py-4 md:px-8 md:py-6 rounded-lg text-xs md:text-sm tracking-normal transition-all ${
                        selected
                          ? "bg-selected text-selected-foreground border border-selected"
                          : "bg-foreground/[0.03] text-foreground border border-border hover:border-foreground/30"
                      }`}
                    >
                      {purpose}
                      {selected && (
                        <span className="absolute top-2 right-2 md:top-3 md:right-3 inline-flex items-center justify-center w-4 h-4 md:w-5 md:h-5 rounded-full bg-selected-foreground text-selected">
                          <Check className="w-2.5 h-2.5 md:w-3 md:h-3" strokeWidth={3} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </motion.div>
            )}

            {/* Step 2: Room Size */}
            {step === 1 && (
              <motion.div
                key="size"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-5"
              >
                {/* Quick-pick chips */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SIZE_CHIPS[areaUnit].map((chip) => {
                    const chipSqm = areaUnit === "sqft" ? chip.midpoint * SQM_PER_SQFT : chip.midpoint;
                    const active =
                      !answers.roomSize?.unsure &&
                      answers.roomSize?.value != null &&
                      Math.abs(answers.roomSize.value - chipSqm) < 0.01;
                    return (
                      <button
                        key={chip.label}
                        onClick={() => selectChip(chip.midpoint, chip.label)}
                        className={`flex flex-col items-start gap-1 px-3 py-2 rounded-lg text-left border transition-colors ${
                          active

                            ? "bg-selected text-selected-foreground border-selected"
                            : "bg-foreground/[0.03] text-foreground border-border hover:border-foreground/30"
                        }`}
                      >
                        <span className="text-xs font-semibold tracking-normal ">
                          {chip.label}
                        </span>
                        <span className={`text-[11px] leading-snug ${active ? "text-selected-foreground/75" : "text-muted-foreground"}`}>
                          {chip.hint}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Unsure */}
                <button
                  onClick={setUnsure}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold tracking-normal border transition-colors ${
                    answers.roomSize?.unsure
                      ? "bg-selected text-selected-foreground border-selected"
                      : "bg-foreground/[0.03] text-foreground border-border hover:border-foreground/30"
                  }`}
                >
                  {answers.roomSize?.unsure && (
                    <Check className="w-3 h-3" strokeWidth={3} />
                  )}
                  Unsure — let the AI estimate
                </button>
              </motion.div>
            )}

            {/* Step 3: Design Style */}
            {step === 2 && (
              <motion.div
                key="style"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Tab toggle */}
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <div className="inline-flex rounded-full border border-border overflow-hidden">
                    <button
                      onClick={() => setStyleTab("presets")}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-normal transition-colors ${
                        styleTab === "presets"
                          ? "bg-selected text-selected-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Preset Styles
                    </button>
                    <button
                      onClick={() => setStyleTab("inspiration")}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-normal transition-colors border-l border-border ${
                        styleTab === "inspiration"
                          ? "bg-selected text-selected-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Add Inspiration
                      {hasInspiration && (
                        <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-selected-foreground text-selected text-[10px]">
                          {answers.inspirationImages!.length}
                        </span>
                      )}
                    </button>
                  </div>
                  {styleTab === "presets" && (
                    <button
                      onClick={() => setStylePage((p) => (p === 0 ? 1 : 0))}
                      className="flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-normal text-muted-foreground border border-border hover:border-foreground/30 hover:text-foreground transition-colors"
                    >
                      <Shuffle className="w-3.5 h-3.5" />
                      View more styles
                    </button>
                  )}
                </div>

                {styleTab === "presets" && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={stylePage}
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.25 }}
                      className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4"
                    >
                      {displayedStyles.map((style) => (
                        <button
                          key={style.name}
                          onClick={() => {
                            setAnswers((p) => ({ ...p, style: style.name }));
                            track("questionnaire_option_selected", {
                              step_number: 3,
                              step_name: "style",
                              option_value: style.name,
                            });
                          }}
                          className={`relative aspect-[4/3] rounded-lg overflow-hidden group transition-all ${
                            answers.style === style.name
                              ? "ring-2 ring-selected"
                              : "ring-1 ring-border hover:ring-foreground/30"
                          }`}
                        >
                          <img
                            src={style.image}
                            alt={style.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <span className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 px-2.5 py-1 rounded-full bg-background/90 backdrop-blur-sm text-foreground text-[10px] sm:text-xs font-semibold tracking-normal ">

                            {style.name}
                          </span>
                        </button>
                      ))}
                    </motion.div>
                  </AnimatePresence>
                )}

                {styleTab === "inspiration" && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.25 }}
                    className="space-y-4"
                  >
                    <p className="text-sm text-muted-foreground">
                      Upload up to 3 reference images — a Pinterest screenshot, a magazine photo, anything that captures the vibe you want. We'll match the colors, materials, and mood.
                    </p>

                    <div className="grid grid-cols-3 gap-3 md:gap-4">
                      {(answers.inspirationImages || []).map((src, idx) => (
                        <div
                          key={idx}
                          className="relative aspect-[4/3] rounded-lg overflow-hidden ring-1 ring-border group"
                        >
                          <img src={src} alt={`Inspiration ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            onClick={() => removeInspiration(idx)}
                            className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-background/80 backdrop-blur-sm border border-border hover:bg-foreground hover:text-background transition-colors"
                            aria-label="Remove inspiration"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}

                      {(answers.inspirationImages?.length ?? 0) < 3 && (
                        <label
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (e.dataTransfer.files?.length) handleInspirationFiles(e.dataTransfer.files);
                          }}
                          className="relative aspect-[4/3] rounded-lg flex flex-col items-center justify-center gap-2 border border-dashed border-border hover:border-foreground/30 hover:bg-foreground/[0.03] cursor-pointer transition-all"
                        >
                          <input
                            type="file"
                            accept="image/*,.heic,.heif"
                            multiple
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={(e) => {
                              if (e.target.files?.length) handleInspirationFiles(e.target.files);
                              e.target.value = "";
                            }}
                          />
                          <Upload className="w-5 h-5 text-muted-foreground" />
                          <span className="text-[10px] sm:text-xs font-semibold tracking-normal text-muted-foreground text-center px-2">
                            Add inspiration
                          </span>
                        </label>
                      )}
                    </div>

                    {answers.style && (
                      <p className="text-xs text-muted-foreground">
                        Combined with preset style: <span className="text-foreground font-semibold">{answers.style}</span>.{" "}
                        <button
                          onClick={() => setAnswers((p) => ({ ...p, style: "" }))}
                          className="underline hover:text-foreground"
                        >
                          Clear preset
                        </button>
                      </p>
                    )}
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Step 4: Existing furniture approach */}
            {step === 3 && (
              <motion.div
                key="furniture-approach"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="grid sm:grid-cols-2 gap-3 md:gap-4"
              >
                {furnitureApproachOptions.map((option) => {
                  const selected = answers.furnitureApproach === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => selectFurnitureApproach(option.value)}
                      className={`relative text-left px-5 py-5 md:px-8 md:py-7 rounded-lg transition-all ${
                        selected
                          ? "bg-selected text-selected-foreground border border-selected"
                          : "bg-foreground/[0.03] text-foreground border border-border hover:border-foreground/30"
                      }`}
                    >
                      <span className="block text-sm md:text-base font-semibold tracking-normal">
                        {option.title}
                      </span>
                      <span
                        className={`block mt-1.5 text-xs md:text-sm leading-relaxed ${
                          selected ? "text-selected-foreground/80" : "text-muted-foreground"
                        }`}
                      >
                        {option.description}
                      </span>
                      {selected && (
                        <span className="absolute top-3 right-3 inline-flex items-center justify-center w-5 h-5 rounded-full bg-selected-foreground text-selected">
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>

      {/* Mobile Navigation - below everything */}
      <div className="flex lg:hidden items-center gap-3 mt-8">
        <button
          onClick={handlePrev}
          className="px-6 py-3 rounded-full border border-foreground text-foreground text-[13px] font-semibold tracking-normal hover:bg-foreground hover:text-background transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!canProceed || submitting} aria-busy={submitting}
          className="flex-1 px-6 py-3 rounded-full bg-primary-strong text-primary-strong-foreground text-[13px] font-semibold tracking-normal shadow-elegant hover:opacity-90 transition-opacity disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none disabled:cursor-not-allowed"
        >
          {nextLabel}
        </button>
      </div>

      {/* Mobile Step Indicators */}
      <div className="flex lg:hidden items-center justify-center gap-3 mt-4">
        {[0, 1, 2, 3].map((s) => (
          <div
            key={s}
            className={`h-px transition-all ${
              s <= step
                ? "w-10 bg-foreground"
                : "w-6 bg-foreground/20"
            }`}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default Questionnaire;
